import { use, useEffect, useState } from 'react';
import { useForm, UseFormReturn, useFormState } from 'react-hook-form';
import { AxiosError } from 'axios';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import { RemoveSystemValidationFailedReason } from 'features/systems/domain/models/remove-system-validation-failed-reason';
import { System } from 'features/systems/domain/models/system';
import { ValidateDeleteSystemResponse } from 'features/systems/domain/models/validate-delete-system-response';
import {
  useDeleteSystem,
  useLazyGetSystem,
  useUpdateSystem,
  useValidateDeleteSystem,
} from 'features/systems/domain/queries/systems-queries';

export interface SystemDetailsHook {
  viewingMode: ViewingMode;

  mutationIsLoading: boolean;
  cancelMutation: () => void;

  selectedSystem: System | undefined;
  setSelectedSystem: any;

  updateSystemFromForm: (fieldValues: any) => void;
  updateSystemIsSuccess: boolean;
  deleteSystem: (system: System) => void;
  refreshSystem: () => void;

  isDetailsOpen: boolean;
  openDetailsInViewingMode: (systemId?: string) => void;
  openDetailsInEditingMode: (systemId?: string) => void;
  closeDetails: () => void;

  isDeleteErrorPopupOpen: boolean;
  systemValidationFailedReason: RemoveSystemValidationFailedReason | undefined;
  closeDeleteErrorPopup: () => void;

  isDeleteConfirmationPopupOpen: boolean;
  closeDeleteConfirmationPopup: () => void;
  deleteConfirmationAction: () => void;

  form: UseFormReturn;
}

const useSystemDetails = (refresh: () => void): SystemDetailsHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);

  const form = useForm({
    mode: 'onBlur',
    defaultValues: {} as Partial<System>,
  });

  const { isDirty } = useFormState({ control: form.control });

  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState<boolean>(false);
  const [isDeleteErrorPopupOpen, setIsDeleteErrorPopupOpen] = useState<boolean>(false);
  const [systemValidationFailedReason, setSystemValidationFailedReason] = useState<RemoveSystemValidationFailedReason>();
  const [deleteConfirmationAction, setDeleteConfirmationAction] = useState(() => () => {});

  const [viewingMode, setViewingMode] = useState<ViewingMode>('viewing');
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [selectedSystem, setSelectedSystem] = useState<System>();
  const [selectedSystemId, setSelectedSystemId] = useState<string>();
  const [mutationIsLoading, setMutationIsLoading] = useState<boolean>(false);

  const {
    refetch: triggerReadSystem,
    data: readSystemData,
    error: readSystemError,
    isError: readSystemIsError,
    isSuccess: readSystemIsSuccess,
    isFetching: readSystemIsFetching,
  } = useLazyGetSystem(selectedSystemId ?? '');

  const {
    mutate: updateSystem,
    data: updateSystemData,
    error: updateSystemError,
    isError: updateSystemIsError,
    isSuccess: updateSystemIsSuccess,
    isPending: updateSystemIsLoading,
  } = useUpdateSystem();

  const {
    mutate: deleteSystemMutation,
    error: deleteSystemError,
    isError: deleteSystemIsError,
    isSuccess: deleteSystemIsSuccess,
    isPending: deleteSystemIsLoading,
  } = useDeleteSystem();

  const {
    mutate: validateDeleteSystemMutation,
    error: validateDeleteSystemError,
    isError: validateDeleteSystemIsError,
    isSuccess: validateDeleteSystemIsSuccess,
  } = useValidateDeleteSystem();

  useEffect(() => {
    if (selectedSystemId) {
      setFormValuesWithSelectedSystem();
      triggerReadSystem();
    }
  }, [selectedSystemId]);

  useEffect(() => {
    setMutationIsLoading(updateSystemIsLoading || deleteSystemIsLoading);
  }, [updateSystemIsLoading, deleteSystemIsLoading]);

  useEffect(() => {
    if (!readSystemIsFetching && readSystemIsSuccess && readSystemData) {
      setSelectedSystem(readSystemData);
      form.reset({
        ...form.getValues(),
        name: readSystemData.name ?? '',
        note: readSystemData.note ?? '',
        organisationUnitId: readSystemData.organisationUnitId ?? undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readSystemIsFetching, readSystemIsSuccess, readSystemData]);

  useEffect(() => {
    if (readSystemIsError && readSystemError) {
      setErrorMessage(readSystemError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readSystemIsError]);

  useEffect(() => {
    if (updateSystemIsSuccess && updateSystemData) {
      setSelectedSystem(updateSystemData);
      setViewingMode('viewing');
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSystemIsSuccess]);

  useEffect(() => {
    if (updateSystemIsError && updateSystemError) {
      setErrorMessage(updateSystemError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSystemIsError]);

  useEffect(() => {
    if (deleteSystemIsError && deleteSystemError) {
      setErrorMessage(deleteSystemError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSystemIsError]);

  useEffect(() => {
    if (deleteSystemIsSuccess) {
      setIsDeleteConfirmationPopupOpen(false);
      setIsDetailsOpen(false);
      refresh();
    }
  }, [deleteSystemIsSuccess]);

  useEffect(() => {
    if (validateDeleteSystemIsSuccess && selectedSystem) {
      setIsDeleteConfirmationPopupOpen(true);
      setDeleteConfirmationAction(() => () => confirmDeleteSystem(selectedSystem));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateDeleteSystemIsSuccess]);

  useEffect(() => {
    if (validateDeleteSystemIsError && selectedSystem) {
      const response = (validateDeleteSystemError as AxiosError<ValidateDeleteSystemResponse>)?.response?.data;
      setSystemValidationFailedReason(response?.validationFailedReason);
      setIsDeleteErrorPopupOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateDeleteSystemIsError]);

  const setFormValuesWithSelectedSystem = () => {
    form.reset({
      ...form.getValues(),
      name: selectedSystem?.name ?? '',
      note: selectedSystem?.note ?? '',
      organisationUnitId: selectedSystem?.organisationUnitId ?? undefined,
    });
  };

  const openDetailsInViewingMode = (systemId?: string) => {
    if (systemId) {
      setSelectedSystemId(systemId);
      setIsDetailsOpen(true);
      setViewingMode('viewing');
    }
  };

  const openDetailsInEditingMode = (systemId?: string) => {
    if (systemId) {
      setSelectedSystemId(systemId);
      setIsDetailsOpen(true);
      setViewingMode('editing');
    }
  };

  const cancelMutation = () => {
    setViewingMode('viewing');
    setFormValuesWithSelectedSystem();
  };

  const closeDetails = () => {
    const showConfirmation = viewingMode === 'editing' && isDirty;
    if (!showConfirmation) {
      setIsDetailsOpen(false);
      setSelectedSystem(undefined);
      setSelectedSystemId(undefined);
      return;
    }

    showDefaultConfirmationPopUp({
      action: () => {
        setIsDetailsOpen(false);
        setSelectedSystem(undefined);
        setSelectedSystemId(undefined);
      },
    });
  };

  const closeDeleteConfirmationPopup = () => setIsDeleteConfirmationPopupOpen(false);

  const closeDeleteErrorPopup = () => {
    setIsDeleteErrorPopupOpen(false);
    setSystemValidationFailedReason(undefined);
  };

  const deleteSystem = (system: System) => {
    setSelectedSystem(system);
    if (system.id) {
      setSystemValidationFailedReason(undefined);
      validateDeleteSystemMutation(system.id);
    }
  };

  const confirmDeleteSystem = (system: System) => {
    if (system.id) {
      deleteSystemMutation(system.id);
    }
    setDeleteConfirmationAction(() => () => {});
    setSelectedSystem(undefined);
    setSelectedSystemId(undefined);
  };

  const refreshSystem = (): void => {
    if (selectedSystem?.id) {
      triggerReadSystem();
    }
  };

  const updateSystemFromForm = (fieldValues: any): void => {
    if (!selectedSystem) return;

    const updatedSystem: System = {
      id: selectedSystem.id,
      gateway: selectedSystem.gateway,
      lastSystemEvent: undefined,
      status: selectedSystem.status,
      type: fieldValues.type,
      unitType: selectedSystem.unitType,
      name: fieldValues.name?.trim(),
      note: fieldValues.note?.trim(),
      macAddress: selectedSystem.macAddress,
      ipAddress: selectedSystem.ipAddress,
      audioAvailable: selectedSystem.audioAvailable,
      audioRecorderAvailable: selectedSystem.audioRecorderAvailable,
      firmwareVersion: selectedSystem.firmwareVersion,
      organisationUnitId: fieldValues.organisationUnitId,
      intercomFunctionalityAvailable: selectedSystem.intercomFunctionalityAvailable,
      ioFunctionalityAvailable: selectedSystem.ioFunctionalityAvailable,
    };

    updateSystem(updatedSystem);
  };

  return {
    viewingMode,
    mutationIsLoading,
    cancelMutation,

    selectedSystem,
    setSelectedSystem,

    updateSystemFromForm,
    updateSystemIsSuccess,
    deleteSystem,
    refreshSystem,

    isDetailsOpen,
    openDetailsInViewingMode,
    openDetailsInEditingMode,
    closeDetails,

    isDeleteErrorPopupOpen,
    systemValidationFailedReason,
    closeDeleteErrorPopup,

    isDeleteConfirmationPopupOpen,
    closeDeleteConfirmationPopup,
    deleteConfirmationAction,

    form,
  };
};

export default useSystemDetails;
